import type { GroundGap } from '../level/segments/SegmentDefinition';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import type { ObstacleInstance } from '../obstacles/ObstacleInstance';

export const DozerPhase = {
  /** Parked in its lane, engine running – an obstacle on foot, a vehicle for a runner who jumps onto it. */
  Parked: 'PARKED',
  /** The runner rides it. */
  Driving: 'DRIVING',
  /** The rider is off – it brakes to a stop and idles there. */
  Abandoned: 'ABANDONED',
} as const;
export type DozerPhase = (typeof DozerPhase)[keyof typeof DozerPhase];

/** Why the rider left the dozer. */
export const DozerExit = {
  /** Jump key while driving. */
  Jumped: 'JUMPED',
  /** A hit threw the rider off (or the run ended on it). */
  Thrown: 'THROWN',
  /** Too little earth: stopped at the pit edge, the rider was catapulted over the pit. */
  Pit: 'PIT',
  /** Over the filled pit: the ride ends, the rider jumps off and runs on. */
  End: 'END',
} as const;
export type DozerExit = (typeof DozerExit)[keyof typeof DozerExit];

/** Numeric pose of the dozer (segment-local) – the presenter interpolates it between fixed steps. */
export interface DozerPose {
  /** Origin (ground centre of the tracks) along the module and across it. */
  z: number;
  x: number;
  /** Distance driven so far – tracks and wheels turn with it. */
  travelled: number;
  /** Forward speed (m/s). */
  speed: number;
}

/**
 * One dozer set piece of a placed module (gameplay state only, no Babylon): where the dozer is, who drives it,
 * the earth it pushed and what happened at the pit. The DozerManager runs it, the DozerPresenter shows it.
 */
export class DozerRide {
  phase: DozerPhase = DozerPhase.Parked;
  /** Heaps the blade pushed so far – the pile in front of it. */
  heapsPushed = 0;
  /** The pit: null = not reached yet, true = filled with the pile, false = too little earth. */
  pitFilled: boolean | null = null;
  readonly pose: DozerPose;
  readonly previous: DozerPose;
  /** Abandoned: where it comes to a stop (origin z, segment-local) and how hard it brakes (m/s²). */
  stopZ = Infinity;
  deceleration = 0;

  constructor(
    readonly segment: SegmentInstance,
    /** The parked dozer as an obstacle (OBS_DOZER_A) – retired from play once the runner is on it. */
    readonly obstacle: ObstacleInstance,
    /** The heaps of earth of the placed layout. */
    readonly heaps: readonly ObstacleInstance[],
    readonly pit: GroundGap | null,
    /** Lane it is parked in. */
    readonly lane: number,
    /** Height of the road it stands on. */
    readonly ground: number,
  ) {
    const start: DozerPose = { z: obstacle.spawnPoint.position.z, x: obstacle.spawnPoint.position.x, travelled: 0, speed: 0 };
    this.pose = { ...start };
    this.previous = { ...start };
  }

  /** Heaps placed in the module (rows the populator left out never count). */
  get heapsTotal(): number {
    return this.heaps.length;
  }

  /** Every heap pushed and the pit filled. */
  get perfect(): boolean {
    return this.pitFilled === true && this.heapsTotal > 0 && this.heapsPushed >= this.heapsTotal;
  }

  /** Track distance of the origin. */
  get distance(): number {
    return this.segment.startDistance + this.pose.z;
  }

  keepPrevious(): void {
    Object.assign(this.previous, this.pose);
  }
}
